import * as genericService from "./genericFormServices";
import type { CreateAttachmentDto } from "../types/MainType.type";
import { formatSignatureUrl, type FetchParams } from "./NetworkService";
import type { LegalAdviceFormElevenData } from "../types/LegalAdvices/LegalAdviceFormEleven.type";

const ENDPOINT = "legal-advice-form-eleven";

export interface LegalAdviceFormElevenFetchResponse {
  data: LegalAdviceFormElevenData[];
  totalItems: number;
}

// ---------------------------------------------------------------------------
// 1. Mapper for Legal Advice Form Eleven
// ---------------------------------------------------------------------------
export const mapLegalAdviceFormElevenBackendToFrontend = (
  item: any
): LegalAdviceFormElevenData => {
  if (!item) return {} as LegalAdviceFormElevenData;

  const form = item.legalAdviceFormEleven || item.formData || item;

  return {
    // Document Level Identifiers
    id: item.id || form.id || "",
    documentNumber: item.documentNumber || "",
    formType: item.formType || "LEGAL_ADVICE_FORM_ELEVEN",
    status: item.status || form.status || "DRAFT",

    // Form-Specific Header Fields
    office: form.office || "",

    // အပိုင်း ၁။ အချက်အလက်များ (၁ မှ ၁၃)
    courtName: form.courtName || "",
    executionCaseNo: form.executionCaseNo || form.caseNo || form.caseNumber || "",
    decreeHolderName: form.decreeHolderName || form.plaintiffName || "",
    judgmentDebtorName: form.judgmentDebtorName || form.defendantName || "",
    representedParty: form.representedParty || "",
    originalCaseDetails: form.originalCaseDetails || "",
    higherCourtAppealStatus: form.higherCourtAppealStatus || "",
    higherCourtDecreeDetails: form.higherCourtDecreeDetails || "",
    executionApplicationDate: form.executionApplicationDate || "",
    courtObjectionStatus: form.courtObjectionStatus || "",
    objectionDetails: form.objectionDetails || "",
    priorDecreeApplicationStatus: form.priorDecreeApplicationStatus || "",
    executionClosedDetails: form.executionClosedDetails || "",
    executionOrderDetails: form.executionOrderDetails || "",

    // အပိုင်း ၂။ သဘောထားမှတ်ချက်နှင့် လက်မှတ်များ (၁၄ မှ ၁၅)
    handlingOfficerOpinion: form.handlingOfficerOpinion || "",
    handlingOfficerName: form.handlingOfficerName || form.prosecutorName || "",
    handlingOfficerSubmitDate:
      form.handlingOfficerSubmitDate || form.prosecutorSubmissionDate || "",
    handlingOfficerSignature: formatSignatureUrl(
      form.handlingOfficerSignature || form.prosecutorSignatureImage
    ),

    inChargeOfficerOpinion:
      form.inChargeOfficerOpinion || form.responsibleOfficerOpinion || "",
    inChargeOfficerName: form.inChargeOfficerName || form.officerName || "",
    inChargeOfficerSubmitDate:
      form.inChargeOfficerSubmitDate || form.officerSubmissionDate || "",
    inChargeOfficerSignature: formatSignatureUrl(
      form.inChargeOfficerSignature || form.officerSignatureImage
    ),

    // Geo Levels
    originGeoLevel: item.originGeoLevel || "",
    targetGeoLevels: Array.isArray(item.targetGeoLevels)
      ? item.targetGeoLevels
      : [],

    // Attachments Array
    attachments: item.attachments || form.attachments || [],

    // Dates & Status Metadata
    rejectionReason: item.rejectionReason || form.rejectionReason || "",
    submitDate: form.inChargeOfficerSubmitDate || form.officerSubmissionDate || "",
    createdAt: item.createdAt || form.createdAt || "",
    updatedAt: item.updatedAt || form.updatedAt || "",
    submittedAt: item.submittedAt || form.submittedAt || "",

    // Relational Metadata
    createdBy: item.createdBy
      ? {
          id: item.createdBy.id,
          name: item.createdBy.name,
          email: item.createdBy.email,
          role: item.createdBy.role,
          position: item.createdBy.position,
        }
      : undefined,
    originOffice: item.originOffice
      ? {
          id: item.originOffice.id,
          name: item.originOffice.name,
          code: item.originOffice.code,
        }
      : undefined,
    stageHistory: Array.isArray(item.stageHistory)
      ? item.stageHistory.map((history: any) => ({
          id: history.id,
          action: history.action,
          comments: history.comments,
          attachments: history.attachments,
          createdAt: history.createdAt,
          performedBy: history.performedBy
            ? {
                id: history.performedBy.id,
                name: history.performedBy.name,
                role: history.performedBy.role,
                position: history.performedBy.position,
                office: history.performedBy.office
                  ? {
                      id: history.performedBy.office.id,
                      name: history.performedBy.office.name,
                      code: history.performedBy.office.code,
                      level: history.performedBy.office.level,
                    }
                  : undefined,
              }
            : undefined,
        }))
      : [],
    auditLogs: Array.isArray(item.auditLogs) ? item.auditLogs : [],
  };
};

// ---------------------------------------------------------------------------
// 2. Custom Filter Function for Legal Advice Form Eleven
// ---------------------------------------------------------------------------
const legalAdviceFormElevenFilterPredicate = (
  app: LegalAdviceFormElevenData,
  params: FetchParams
): boolean => {
  const { searchTerm, selectedStatus, selectedDate } = params;

  const matchesStatus = !selectedStatus || app.status === selectedStatus;
  const formattedAppDate = app.submitDate || app.createdAt || "";
  const matchesDate =
    !selectedDate || Boolean(formattedAppDate.includes(selectedDate));

  const term = (searchTerm || "").toLowerCase().trim();

  const matchesGeneralSearch =
    !term ||
    Boolean(app.documentNumber && app.documentNumber.toLowerCase().includes(term)) ||
    // Boolean(app.lawRegisterNo && app.lawRegisterNo.toLowerCase().includes(term)) ||
    Boolean(app.executionCaseNo && app.executionCaseNo.toLowerCase().includes(term)) ||
    Boolean(app.courtName && app.courtName.toLowerCase().includes(term)) ||
    Boolean(app.decreeHolderName && app.decreeHolderName.toLowerCase().includes(term)) ||
    Boolean(app.judgmentDebtorName && app.judgmentDebtorName.toLowerCase().includes(term)) ||
    Boolean(app.handlingOfficerName && app.handlingOfficerName.toLowerCase().includes(term)) ||
    Boolean(app.inChargeOfficerName && app.inChargeOfficerName.toLowerCase().includes(term));

  return Boolean(matchesStatus && matchesDate && matchesGeneralSearch);
};

// ---------------------------------------------------------------------------
// 3. API Request Functions
// ---------------------------------------------------------------------------

export const fetchLegalAdviceFormElevenApplications = (
  params: FetchParams
): Promise<LegalAdviceFormElevenFetchResponse> =>
  genericService.fetchApplications<LegalAdviceFormElevenData>(
    ENDPOINT,
    params,
    mapLegalAdviceFormElevenBackendToFrontend,
    legalAdviceFormElevenFilterPredicate
  );

export const fetchLegalAdviceFormElevenById = (
  id: string
): Promise<LegalAdviceFormElevenData> =>
  genericService.fetchApplicationById<LegalAdviceFormElevenData>(
    ENDPOINT,
    id,
    mapLegalAdviceFormElevenBackendToFrontend
  );

export const saveLegalAdviceFormElevenDraft = (
  id: string | null,
  data: Partial<LegalAdviceFormElevenData>
) => genericService.saveDraft<LegalAdviceFormElevenData>(ENDPOINT, id, data);

export const submitLegalAdviceFormElevenForm = (
  id: string,
  data: LegalAdviceFormElevenData
) => genericService.submitForm<LegalAdviceFormElevenData>(ENDPOINT, id, data);

export const deleteLegalAdviceFormElevenForm = (id: string) =>
  genericService.deleteForm(ENDPOINT, id);

// ---------------------------------------------------------------------------
// 4. Superior Stage Actions
// ---------------------------------------------------------------------------
export const startReviewLegalAdviceForm = (id: string) =>
  genericService.startReviewForm(id);

export const attachLegalAdviceForm = (
  id: string,
  comments?: string,
  attachments?: CreateAttachmentDto[],
  targetGeoLevels?: string[]
) => genericService.attachForm(id, comments, attachments, targetGeoLevels);

export const uploadFileService = (file: File) =>
  genericService.uploadFile(file);